import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Save, X, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { AdminShell } from "./AdminShell";
import { AInput, ATextarea, ASelect, AToggle, ImageInput } from "@/components/admin/AdminInputs";

const EMPTY = {
  name: "", location: "", type: "", status: "Completed", summary: "",
  hero_img: "", before_img: "", during_img: "", after_img: "",
  the_property: "", why_we_bought: "", starting_condition: "", the_renovation: "",
  problems_encountered: "", how_we_solved: "", renovation_scope: "", outcome: "",
  lessons_learned: "", purchase_price: "", renovation_cost: "", gdv: "",
  show_financials: false, gallery: [], published: true, order: 0,
};

function Editor({ initial, onClose, onSaved }) {
  const [f, setF] = useState({ ...EMPTY, ...initial });
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!f.name) return toast.error("Name is required");
    setBusy(true);
    try {
      const body = { ...f, order: Number(f.order) || 0 };
      if (initial?.slug) await api.put(`/projects/${initial.slug}`, body);
      else await api.post("/projects", body);
      toast.success("Project saved");
      onSaved();
    } catch (e) { toast.error("Save failed"); } finally { setBusy(false); }
  };

  return (
    <div className="bg-[#0F2E23] rounded-xl border border-brass/30 p-6 mb-6" data-testid="project-editor">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-serif text-white">{initial?.slug ? "Edit project" : "New project"}</h3>
        <button onClick={onClose} className="text-emerald-100/60 hover:text-white"><X className="w-5 h-5" /></button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <AInput label="Name" value={f.name} onChange={set("name")} testId="proj-name" />
        <AInput label="Location" value={f.location} onChange={set("location")} testId="proj-location" />
        <AInput label="Type (e.g. Full Modernisation)" value={f.type} onChange={set("type")} />
        <ASelect label="Status" value={f.status} onChange={set("status")} options={["Completed", "In Progress", "In Planning", "Coming Soon"]} />
      </div>
      <div className="mt-4"><ATextarea label="Summary (card text)" value={f.summary} onChange={set("summary")} testId="proj-summary" /></div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <ImageInput label="Hero image" value={f.hero_img} onChange={set("hero_img")} testId="proj-hero-upload" />
        <ImageInput label="Before image" value={f.before_img} onChange={set("before_img")} />
        <ImageInput label="During image" value={f.during_img} onChange={set("during_img")} />
        <ImageInput label="After image" value={f.after_img} onChange={set("after_img")} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <ATextarea label="The property" value={f.the_property} onChange={set("the_property")} />
        <ATextarea label="Why we bought it" value={f.why_we_bought} onChange={set("why_we_bought")} />
        <ATextarea label="Starting condition" value={f.starting_condition} onChange={set("starting_condition")} />
        <ATextarea label="The renovation" value={f.the_renovation} onChange={set("the_renovation")} />
        <ATextarea label="Problems encountered" value={f.problems_encountered} onChange={set("problems_encountered")} />
        <ATextarea label="How we solved them" value={f.how_we_solved} onChange={set("how_we_solved")} />
        <ATextarea label="Renovation scope" value={f.renovation_scope} onChange={set("renovation_scope")} />
        <ATextarea label="Outcome" value={f.outcome} onChange={set("outcome")} />
      </div>
      <div className="mt-4"><ATextarea label="Lessons learned" value={f.lessons_learned} onChange={set("lessons_learned")} /></div>
      <div className="mt-4 border-t border-white/10 pt-4">
        <AToggle label="Show financial figures on the case study" value={f.show_financials} onChange={set("show_financials")} testId="proj-show-financials" />
        {f.show_financials && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
            <AInput label="Purchase price" value={f.purchase_price} onChange={set("purchase_price")} />
            <AInput label="Renovation cost" value={f.renovation_cost} onChange={set("renovation_cost")} />
            <AInput label="End value (GDV)" value={f.gdv} onChange={set("gdv")} />
          </div>
        )}
      </div>
      <div className="flex items-center gap-4 mt-4">
        <div className="w-28"><AInput label="Order" type="number" value={f.order} onChange={set("order")} /></div>
        <div className="pt-5"><AToggle label="Published" value={f.published} onChange={set("published")} testId="proj-published" /></div>
      </div>
      <div className="flex gap-3 mt-5">
        <button onClick={save} disabled={busy} data-testid="proj-save" className="flex items-center gap-1.5 bg-brass hover:bg-brass-600 text-emerald-950 font-semibold rounded-full px-6 py-2 text-sm"><Save className="w-4 h-4" /> {busy ? "Saving…" : "Save project"}</button>
        <button onClick={onClose} className="text-emerald-100/70 hover:text-white text-sm">Cancel</button>
      </div>
    </div>
  );
}

export default function AdminProjects() {
  const [projects, setProjects] = useState([]);
  const [editing, setEditing] = useState(null); // object or 'new' or null

  const load = async () => { const { data } = await api.get("/admin/projects"); setProjects(data); };
  useEffect(() => { load(); }, []);

  const remove = async (slug) => {
    if (!window.confirm("Delete this project?")) return;
    try { await api.delete(`/projects/${slug}`); toast.success("Deleted"); load(); } catch (e) { toast.error("Delete failed"); }
  };

  return (
    <AdminShell title="Projects">
      {!editing && (
        <button onClick={() => setEditing("new")} data-testid="project-new" className="flex items-center gap-1.5 bg-brass hover:bg-brass-600 text-emerald-950 font-semibold rounded-full px-5 py-2 text-sm mb-6"><Plus className="w-4 h-4" /> New project</button>
      )}
      {editing && <Editor initial={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-projects-list">
        {projects.map((p) => (
          <div key={p.slug} className="bg-[#0F2E23] rounded-xl border border-white/10 overflow-hidden">
            <div className="h-32 bg-[#091E16]">{p.hero_img && <img src={p.hero_img} alt={p.name} className="w-full h-full object-cover" />}</div>
            <div className="p-4">
              <div className="flex items-center justify-between">
                <h3 className="text-white font-serif">{p.name}</h3>
                <span className="text-[11px] text-emerald-100/50 flex items-center gap-1">{p.published ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5 text-amber-300" />}</span>
              </div>
              <p className="text-xs text-emerald-100/50 mt-0.5">{p.location} · {p.status}</p>
              <div className="flex gap-2 mt-3">
                <button onClick={() => setEditing(p)} data-testid={`project-edit-${p.slug}`} className="flex items-center gap-1 text-xs bg-white/5 hover:bg-white/10 text-white rounded-lg px-3 py-1.5"><Pencil className="w-3.5 h-3.5" /> Edit</button>
                <button onClick={() => remove(p.slug)} data-testid={`project-delete-${p.slug}`} className="flex items-center gap-1 text-xs bg-red-500/10 hover:bg-red-500/20 text-red-300 rounded-lg px-3 py-1.5"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </AdminShell>
  );
}
